import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, shareReplay } from 'rxjs';
import { environment } from '#environments';
import { DoaLimit, CreateDoaLimit, UpdateDoaLimit, PaginatedResult, ChevronProjectManagerCandidate } from '#models';

export interface DoaLimitFilters {
    search?: string;
    page?: number;
    pageSize?: number;
}

@Injectable({ providedIn: 'root' })
export class DoaLimitService {
    private _http = inject(HttpClient);
    private _base = `${environment.apiBaseUrl}/v1/doa-limits`;

    /** Cached stream of eligible Chevron Project Manager candidates for use in dropdowns. */
    private _projectManagerCandidates$: Observable<ChevronProjectManagerCandidate[]> | null = null;

    /** Returns active AppUsers eligible to be a Remediation's Chevron Project Manager; result is cached for the lifetime of the service instance. */
    getProjectManagerCandidates(): Observable<ChevronProjectManagerCandidate[]> {
        this._projectManagerCandidates$ ??= this._http
            .get<ChevronProjectManagerCandidate[]>(`${this._base}/project-managers`)
            .pipe(shareReplay(1));
        return this._projectManagerCandidates$;
    }

    getAll(filters: DoaLimitFilters = {}): Observable<PaginatedResult<DoaLimit>> {
        const params: Record<string, string> = {};
        if (filters.search) params['search'] = filters.search;
        if (filters.page != null) params['page'] = String(filters.page);
        if (filters.pageSize != null) params['pageSize'] = String(filters.pageSize);
        return this._http.get<PaginatedResult<DoaLimit>>(this._base, { params });
    }

    getById(doaLimitId: string): Observable<DoaLimit> {
        return this._http.get<DoaLimit>(`${this._base}/${doaLimitId}`);
    }

    create(dto: CreateDoaLimit): Observable<DoaLimit> {
        return this._http.post<DoaLimit>(this._base, dto);
    }

    update(doaLimitId: string, dto: UpdateDoaLimit): Observable<DoaLimit> {
        return this._http.put<DoaLimit>(`${this._base}/${doaLimitId}`, dto);
    }

    delete(doaLimitId: string): Observable<void> {
        return this._http.delete<void>(`${this._base}/${doaLimitId}`);
    }
}
